<script setup lang="ts">
import { ref, computed, nextTick, watch, onBeforeUnmount } from 'vue'
import { MessageOutlined, SendOutlined, AudioOutlined, CloseOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons-vue'
import { message as AMessage } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/store/user'
import { useAvatar } from '@/composables/useAvatar'
import { useWebSocket } from '@/composables/useWebSocket'
import type { WebSocketConfig } from '@/store/user'
import type { Rule } from 'ant-design-vue/es/form'
import { getConnectionStatus, waitForWorkbenchSessionInit } from '@/services/websocket'
import {
  closeWorkbenchConversation,
  createWorkbenchClientInstanceId,
  createWorkbenchThreadId,
  isTerminalWorkbenchSession,
  openWorkbenchConversation,
  resolveWorkbenchConversation,
  type WorkbenchConversationSession,
} from '@/services/workbenchConversation'

const { t } = useI18n()
const userStore = useUserStore()
const { getAvatarUrl } = useAvatar()

// WebSocket 连接
const {
  isConnected,
  connectionStatus,
  messages: wsMessages,
  connect,
  disconnect,
  sendText,
  startRecording: wsStartRecording,
  stopRecording: wsStopRecording,
} = useWebSocket()

const unifiedSession = ref<WorkbenchConversationSession | null>(null)
const lifecycleError = ref('')
const workbenchClientInstanceId = createWorkbenchClientInstanceId()
const workbenchThreadId = createWorkbenchThreadId(userStore.userInfo?.userId)

// 聊天窗口状态
const chatVisible = ref(false)
const inputMessage = ref('')
const isVoiceMode = ref(false)
const isRecording = ref(false)
const chatContentRef = ref<HTMLElement>()
let chatOperationGeneration = 0

/** 关闭或账号变化使旧异步输入失效，迟到的 REST、建联和绑定结果不得继续发送。 */
const currentChatOperation = () => {
  const generation = chatOperationGeneration
  const token = userStore.token
  return () => chatVisible.value && generation === chatOperationGeneration && token === userStore.token
}

// 设置抽屉状态
const settingsVisible = ref(false)
const settingsForm = ref<WebSocketConfig>({
  url: userStore.wsConfig.url,
  deviceId: userStore.wsConfig.deviceId || 'web_test',
  deviceName: userStore.wsConfig.deviceName || 'Web用户',
  autoConnect: userStore.wsConfig.autoConnect
})

// 表单验证规则
const rules: Record<string, Rule[]> = {
  url: [
    { required: true, message: t('validation.enterWebSocketUrl'), trigger: 'blur' },
    {
      pattern: /^wss?:\/\/.+/,
      message: t('validation.enterValidWebSocketUrl'),
      trigger: 'blur'
    }
  ],
  deviceId: [
    { required: true, message: t('validation.enterDeviceId'), trigger: 'blur' }
  ]
}

// 头像
const userAvatar = computed(() => getAvatarUrl(userStore.userInfo?.avatar))
const aiAvatar = computed(() => '/robot-avatar-default.png') // 默认AI头像

// WebSocket 配置（从 store 获取）
const wsConfig = computed(() => ({
  url: userStore.wsConfig.url,
  // 工作台传输不能复用真实设备 ID，避免误修改设备在线状态和借用设备上下文。
  deviceId: `web_${workbenchClientInstanceId.replace(/-/g, '')}`,
  deviceName: userStore.wsConfig.deviceName || userStore.userInfo?.name || 'Web用户',
  token: userStore.token,
  sourceType: 'WORKBENCH' as const,
  conversationSessionId: unifiedSession.value?.sessionId,
  threadId: workbenchThreadId,
}))

/** 必须先建 canonical 会话再开传输，服务不可用时禁止静默降级。 */
const ensureUnifiedSession = async (
  verifiedSession?: WorkbenchConversationSession | null,
  isCurrent: () => boolean = () => true,
): Promise<boolean> => {
  try {
    lifecycleError.value = ''
    const resolved = verifiedSession === undefined
      ? await resolveWorkbenchConversation({ sourceType: 'WORKBENCH', threadId: workbenchThreadId })
      : verifiedSession
    if (!isCurrent()) return false
    const previous = unifiedSession.value
    const nextSession = resolved || await openWorkbenchConversation({
      openRequestId: crypto.randomUUID(),
      clientInstanceId: workbenchClientInstanceId,
      sourceType: 'WORKBENCH',
      transport: 'WEBSOCKET',
      threadId: workbenchThreadId,
      ...(previous ? { previousSessionId: previous.sessionId } : {}),
    })
    if (!isCurrent()) return false
    unifiedSession.value = nextSession
    return !isTerminalWorkbenchSession(unifiedSession.value)
  } catch {
    if (!isCurrent()) return false
    lifecycleError.value = '统一会话服务暂不可用'
    unifiedSession.value = null
    return false
  }
}

/** session_init 的权威 connectionId 用于输入与关闭 fencing。 */
const sessionBound = ref(false)
const bindingPending = ref(false)
const awaitSessionBinding = async (isCurrent: () => boolean = () => true): Promise<boolean> => {
  sessionBound.value = false
  bindingPending.value = true
  try {
    const bound = await waitForWorkbenchSessionInit()
    if (!isCurrent()) return false
    if (!unifiedSession.value || bound.conversationSessionId !== unifiedSession.value.sessionId || !bound.connectionId) {
      lifecycleError.value = '工作台会话初始化失败，请重新连接'
      return false
    }
    unifiedSession.value = {
      ...unifiedSession.value,
      connectionId: bound.connectionId,
      threadId: bound.threadId || unifiedSession.value.threadId,
    }
    sessionBound.value = true
    lifecycleError.value = ''
    return true
  } catch {
    if (!isCurrent()) return false
    lifecycleError.value = '工作台会话初始化失败，请重新连接'
    return false
  } finally {
    bindingPending.value = false
  }
}

/** TCP 已打开不代表 canonical session_init 成功；只有绑定代次齐全才允许在线和发消息。 */
const connectionReady = computed(() => isConnected.value && sessionBound.value && !!unifiedSession.value?.connectionId)
watch(isConnected, (connected) => { if (!connected) sessionBound.value = false })

// 账号退出由真实主连接原因结束旧会话，并清空客户端绑定。
watch(() => userStore.token, (token, previousToken) => {
  if (token === previousToken) return
  chatOperationGeneration++
  void stopRecording()
  disconnect('LOGOUT')
  sessionBound.value = false
  unifiedSession.value = null
})

// 监听消息变化并滚动到底部
watch(() => wsMessages.value.length, () => {
  scrollToBottom()
})

const connectOnChatOpen = async () => {
  if (!userStore.wsConfig.autoConnect) {
    return
  }

  // 设备控制台与浮动聊天共用物理连接；已有设备连接不能直接冒充工作台会话。
  if (isConnected.value && getConnectionStatus().sourceType !== 'WORKBENCH') {
    disconnect()
    sessionBound.value = false
  }
  if (isConnected.value && sessionBound.value) return

  try {
    if (!await ensureUnifiedSession()) return
    const success = await connect(wsConfig.value)
    if (!success || !await awaitSessionBinding()) {
      if (success) disconnect()
      AMessage.warning(t('system.tip.wsConnectionFailed'))
    }
  } catch (error) {
    console.error('连接WebSocket失败:', error)
  }
}

// 切换聊天窗口
const toggleChat = async () => {
  if (chatVisible.value) {
    await closeChat()
    return
  }
  chatVisible.value = true
  if (chatVisible.value) {
    await connectOnChatOpen()
    nextTick(() => {
      scrollToBottom()
    })
  }
}

// 关闭聊天窗口
const closeChat = async () => {
  chatOperationGeneration++
  chatVisible.value = false
  void stopRecording()
  const current = unifiedSession.value
  if (current && !isTerminalWorkbenchSession(current)) {
    try {
      unifiedSession.value = await closeWorkbenchConversation(current, 'USER_CLOSE')
    } catch {
      lifecycleError.value = '会话关闭失败，请重试'
      return
    }
  }
  disconnect('USER_CLOSE')
  sessionBound.value = false
  unifiedSession.value = null
}

// 滚动到底部
const scrollToBottom = () => {
  nextTick(() => {
    if (chatContentRef.value) {
      chatContentRef.value.scrollTop = chatContentRef.value.scrollHeight
    }
  })
}

const inputValidationPending = ref(false)
const connectionPending = computed(() => inputValidationPending.value || bindingPending.value)

// 每次真实输入前核对权威会话，物理连接仍打开不代表旧 UUID 仍在有效期内。
const ensureConnection = async (): Promise<boolean> => {
  if (inputValidationPending.value || bindingPending.value) return false
  if (isConnected.value && getConnectionStatus().sourceType !== 'WORKBENCH') {
    disconnect()
    sessionBound.value = false
  }
  const isCurrent = currentChatOperation()
  inputValidationPending.value = true
  try {
    if (connectionReady.value) {
      const resolved = await resolveWorkbenchConversation({ sourceType: 'WORKBENCH', threadId: workbenchThreadId })
      if (!isCurrent()) return false
      const current = unifiedSession.value
      if (resolved && !isTerminalWorkbenchSession(resolved)
        && resolved.sessionId === current?.sessionId && resolved.connectionId === current.connectionId) {
        unifiedSession.value = resolved
        lifecycleError.value = ''
        return true
      }
      // 已过期的连接必须先断开；新 UUID 关联旧会话，线程和未发送输入仍保留。
      sessionBound.value = false
      disconnect()
      if (!await ensureUnifiedSession(resolved && !isTerminalWorkbenchSession(resolved) ? resolved : null, isCurrent)) return false
    } else {
      if (!await ensureUnifiedSession(undefined, isCurrent)) return false
    }
    if (!isCurrent()) return false
    const success = await connect(wsConfig.value)
    if (!isCurrent()) {
      disconnect()
      return false
    }
    if (!success) {
      AMessage.error(t('system.tip.notConnected'))
      return false
    }
    if (!await awaitSessionBinding(isCurrent)) {
      disconnect()
      return false
    }
    return isCurrent() && connectionReady.value
  } catch {
    if (!isCurrent()) return false
    // 无法验证时不能继续向旧连接发消息，也不能把校验错误中的地址或令牌展示给用户。
    lifecycleError.value = '统一会话服务暂不可用'
    sessionBound.value = false
    disconnect()
    return false
  } finally {
    inputValidationPending.value = false
  }
}

// 发送文本消息
const sendTextMessage = async () => {
  const draft = inputMessage.value
  const text = draft.trim()
  if (!text) return
  const isCurrent = currentChatOperation()

  // 确保连接
  const connected = await ensureConnection()
  if (!connected || !isCurrent()) return

  // 发送到服务器
  const success = sendText(text, {
    conversationSessionId: unifiedSession.value?.sessionId,
    connectionId: unifiedSession.value?.connectionId,
    threadId: unifiedSession.value?.threadId || workbenchThreadId,
    sourceType: 'WORKBENCH',
  })

  if (success) {
    // 校验期间用户可能已写下一条，成功只清空本次发送时捕获的原始草稿。
    if (inputMessage.value === draft) inputMessage.value = ''
    scrollToBottom()
  } else {
    AMessage.error(t('system.tip.sendFailed'))
  }
}

// 处理回车键
const handleEnterKey = (e: KeyboardEvent) => {
  if (!e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
    e.preventDefault()
    sendTextMessage()
  }
}

// 切换输入模式
const toggleInputMode = () => {
  isVoiceMode.value = !isVoiceMode.value
}

let recordingPressGeneration = 0
let recordingPressed = false
let recordingStartPending = false

// 开始录音；松手和离开按钮会撤销按住意图，不能在异步校验后迟到开启麦克风。
const startRecording = async () => {
  if (isRecording.value || recordingStartPending) return
  recordingPressed = true
  recordingStartPending = true
  const generation = ++recordingPressGeneration
  const isCurrent = currentChatOperation()
  const stillPressed = () => recordingPressed && generation === recordingPressGeneration && isCurrent()
  try {
    const connected = await ensureConnection()
    if (!connected || !stillPressed()) return
    isRecording.value = true
    await wsStartRecording()
    // 编码器或 getUserMedia 也可能晚完成；松手后的晚启动必须再次释放资源。
    if (!stillPressed()) {
      isRecording.value = false
      await wsStopRecording()
    }
  } catch {
    isRecording.value = false
    if (stillPressed()) AMessage.error(t('system.tip.recordingFailed'))
  } finally {
    recordingStartPending = false
  }
}

// 停止录音
const stopRecording = async () => {
  recordingPressed = false
  recordingPressGeneration++
  if (!isRecording.value) return

  try {
    isRecording.value = false
    await wsStopRecording()
  } catch (error) {
    AMessage.error(t('system.tip.stopRecordingFailed'))
  }
}

onBeforeUnmount(() => {
  chatOperationGeneration++
  void stopRecording()
})

// 清空消息
const clearMessages = () => {
  wsMessages.value.splice(0, wsMessages.value.length)
}

// 打开设置抽屉
const openSettings = () => {
  console.log('打开设置抽屉')
  settingsForm.value = {
    url: userStore.wsConfig.url,
    deviceId: userStore.wsConfig.deviceId || userStore.userInfo?.email || 'web_test',
    deviceName: userStore.wsConfig.deviceName || 'Web用户',
    autoConnect: userStore.wsConfig.autoConnect
  }
  settingsVisible.value = true
  console.log('settingsVisible:', settingsVisible.value)
}

// 保存设置
const saveSettings = async () => {
  try {
    // 更新 store
    userStore.updateWsConfig(settingsForm.value)

    AMessage.success('配置已保存')

    // 如果已连接，断开重连
    if (isConnected.value) {
      sessionBound.value = false
      disconnect()
      await new Promise(resolve => setTimeout(resolve, 500))

      if (settingsForm.value.autoConnect) {
        if (!await ensureUnifiedSession()) return
        const success = await connect(wsConfig.value)
        if (success && await awaitSessionBinding()) {
          AMessage.success('已重新连接')
        } else if (success) {
          disconnect()
          lifecycleError.value = '工作台会话初始化失败，请重新连接'
        }
      }
    }

    settingsVisible.value = false
  } catch {
    AMessage.error('配置保存或连接失败，请重试')
  }
}

// 取消设置
const cancelSettings = () => {
  settingsVisible.value = false
}

// 测试连接
const testConnection = async () => {
  try {
    AMessage.loading({ content: '测试连接中...', key: 'testConn', duration: 0 })

    // 临时使用表单中的配置测试
    if (!await ensureUnifiedSession()) return
    const testConfig = { ...wsConfig.value, url: settingsForm.value.url }

    // 断开当前连接
    if (isConnected.value) {
      sessionBound.value = false
      disconnect()
    }

    // 测试新配置
    const success = await connect(testConfig)

    if (success && await awaitSessionBinding()) {
      AMessage.success({ content: '连接测试成功！', key: 'testConn' })
    } else {
      if (success) disconnect()
      AMessage.error({ content: '连接测试失败', key: 'testConn' })
    }
  } catch {
    AMessage.error({ content: t('system.tip.wsConnectionFailed'), key: 'testConn' })
  }
}

// 格式化时间
const formatTime = (date: Date) => {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)

  if (minutes < 1) return '刚刚'
  if (minutes < 60) return `${minutes}分钟前`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}小时前`

  return date.toLocaleString()
}

// 是否显示时间戳
const showTimestamp = (index: number) => {
  if (index === 0) return true
  const prevMsg = wsMessages[index - 1]
  const currMsg = wsMessages[index]
  if (!prevMsg || !currMsg) return false
  const timeDiff = currMsg.timestamp.getTime() - prevMsg.timestamp.getTime()
  return timeDiff > 5 * 60 * 1000 // 超过5分钟显示时间
}

// 连接状态文本
const connectionStatusText = computed(() => {
  if (connectionReady.value) {
    return '在线'
  }
  if (bindingPending.value || (isConnected.value && !lifecycleError.value)) return '会话初始化中'
  if (lifecycleError.value) return lifecycleError.value
  return connectionStatus.value
})

// 连接状态类型
const connectionStatusDot = computed(() => {
  return connectionReady.value ? 'online' : 'offline'
})

// 重试连接
const handleRetryConnect = async () => {
  if (connectionPending.value) return
  try {
    AMessage.loading({ content: '正在尝试连接...', key: 'retryConn', duration: 0 })
    // 重连与发送采用同一套会话有效期校验和 connectionId 绑定。
    if (await ensureConnection()) {
      AMessage.success({ content: '连接成功！', key: 'retryConn' })
    } else {
      AMessage.error({ content: t('system.tip.wsConnectionFailed'), key: 'retryConn' })
    }
  } catch {
    AMessage.error({ content: t('system.tip.wsConnectionFailed'), key: 'retryConn' })
  }
}
</script>

<template>
  <div class="floating-chat">
    <!-- 浮动按钮 -->
    <a-float-button
      :type="chatVisible ? 'default' : 'primary'"
      @click="toggleChat"
      :style="{ right: '24px', bottom: '24px' }"
    >
      <template #icon>
        <MessageOutlined v-if="!chatVisible" />
        <CloseOutlined v-else />
      </template>
    </a-float-button>

    <!-- 聊天窗口 -->
    <transition name="chat-slide">
      <div v-if="chatVisible" class="chat-window">
        <!-- 头部 -->
        <div class="chat-header">
          <div class="header-info">
            <a-avatar :src="aiAvatar" :size="36" />
            <div class="header-text">
              <div class="header-title">AI 助手</div>
              <div class="header-status">
                <span class="status-dot" :class="connectionStatusDot"></span>
                {{ connectionStatusText }}
              </div>
            </div>
          </div>
          <div class="header-actions">
            <a-button
              type="text"
              size="small"
              @click="openSettings"
              title="设置"
            >
              <template #icon>
                <SettingOutlined />
              </template>
            </a-button>
            <a-button
              type="text"
              size="small"
              @click="clearMessages"
              title="清空消息"
            >
              <template #icon>
                <DeleteOutlined />
              </template>
            </a-button>
            <a-button
              type="text"
              size="small"
              @click="closeChat"
            >
              <template #icon>
                <CloseOutlined />
              </template>
            </a-button>
          </div>
        </div>

        <div v-if="wsMessages.length > 0 && !connectionReady" class="connection-recovery" role="status">
          <span>{{ connectionStatusText }}</span>
          <a-button size="small" :disabled="connectionPending" @click="handleRetryConnect">重新连接</a-button>
        </div>

        <!-- 消息区域 -->
        <div ref="chatContentRef" class="chat-content">
          <div v-if="wsMessages.length === 0" class="empty-chat">
            <a-empty>
              <template #image>
                <MessageOutlined :style="{ fontSize: '48px', color: '#d9d9d9' }" />
              </template>
              <template #description>
                <div v-if="!connectionReady" class="connect-guide">
                  <p>{{ connectionStatusText }}</p>
                  <a-space>
                    <a-button type="primary" size="small" :disabled="connectionPending" @click="handleRetryConnect">重试</a-button>
                    <a-button size="small" @click="openSettings">设置</a-button>
                  </a-space>
                </div>
                <span v-else>暂无对话记录</span>
              </template>
            </a-empty>
          </div>
          <div v-else class="chat-messages">
            <div v-for="(message, index) in wsMessages" :key="message.id">
              <!-- 时间戳 -->
              <div v-if="showTimestamp(index)" class="message-timestamp">
                {{ formatTime(message.timestamp) }}
              </div>

              <!-- 消息内容 -->
              <div class="message-wrapper" :class="{ 'user-message': message.isUser, 'ai-message': !message.isUser }">
                <!-- 头像 -->
                <div class="message-avatar">
                  <a-avatar :src="message.isUser ? userAvatar : aiAvatar" :size="32" />
                </div>

                <!-- 消息气泡 -->
                <div class="message-content">
                  <div class="message-bubble">
                    <div class="message-text">{{ message.content }}</div>
                  </div>
                  <div v-if="message.isLoading" class="loading-indicator">
                    <a-spin size="small" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 输入区域 -->
        <div class="chat-input">
          <div class="input-wrapper">
            <!-- 模式切换按钮 -->
            <a-button
              type="text"
              class="mode-toggle"
              :class="{ active: isVoiceMode }"
              @click="toggleInputMode"
            >
              <template #icon>
                <AudioOutlined v-if="isVoiceMode" />
                <MessageOutlined v-else />
              </template>
            </a-button>

            <!-- 文本输入 -->
            <a-textarea
              v-if="!isVoiceMode"
              v-model:value="inputMessage"
              placeholder="输入消息..."
              :auto-size="{ minRows: 1, maxRows: 3 }"
              :bordered="false"
              @keypress.enter="handleEnterKey"
            />

            <!-- 语音输入按钮 -->
            <a-button
              v-else
              class="record-button"
              :class="{ recording: isRecording }"
              type="primary"
              @mousedown="startRecording"
              @mouseup="stopRecording"
              @mouseleave="stopRecording"
              @touchstart="startRecording"
              @touchend="stopRecording"
            >
              {{ isRecording ? '松开结束' : '按住说话' }}
            </a-button>

            <!-- 发送按钮 -->
            <a-button
              v-if="!isVoiceMode"
              type="primary"
              class="send-button"
              :disabled="!inputMessage.trim() || connectionPending"
              @click="sendTextMessage"
            >
              <template #icon>
                <SendOutlined />
              </template>
            </a-button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 设置抽屉 -->
    <a-drawer
      v-model:open="settingsVisible"
      title="聊天设置"
      placement="right"
      :width="360"
      :z-index="2000"
    >
      <a-form
        :model="settingsForm"
        :rules="rules"
        layout="vertical"
      >
        <!-- WebSocket 地址 -->
        <a-form-item label="服务器地址" name="url">
          <a-input
            v-model:value="settingsForm.url"
            placeholder="/ws/afsa/v1/"
          />
          <template #extra>
            <div class="form-tip">
              WebSocket 服务器地址
            </div>
          </template>
        </a-form-item>

        <!-- 设备 ID -->
        <a-form-item label="设备 ID" name="deviceId">
          <a-input
            v-model:value="settingsForm.deviceId"
            placeholder="web_test"
          />
          <template #extra>
            <div class="form-tip">
              设备的唯一标识符
            </div>
          </template>
        </a-form-item>

        <!-- 设备名称 -->
        <a-form-item label="设备名称">
          <a-input
            v-model:value="settingsForm.deviceName"
            placeholder="Web用户"
          />
          <template #extra>
            <div class="form-tip">
              用于显示的设备名称
            </div>
          </template>
        </a-form-item>

        <!-- 自动连接 -->
        <a-form-item label="自动连接">
          <a-switch v-model:checked="settingsForm.autoConnect">
            <template #checkedChildren>开启</template>
            <template #unCheckedChildren>关闭</template>
          </a-switch>
          <div class="form-tip" style="margin-top: 8px;">
            打开聊天窗口时自动连接
          </div>
        </a-form-item>

        <!-- 连接状态 -->
        <a-form-item label="当前状态">
          <a-tag :color="connectionReady ? 'success' : 'default'">
            {{ connectionStatusText }}
          </a-tag>
        </a-form-item>
      </a-form>

      <template #footer>
        <div class="drawer-footer">
          <a-space>
            <a-button @click="cancelSettings">
              取消
            </a-button>
            <a-button type="dashed" @click="testConnection">
              测试连接
            </a-button>
            <a-button type="primary" @click="saveSettings">
              保存
            </a-button>
          </a-space>
        </div>
      </template>
    </a-drawer>
  </div>
</template>

<style scoped lang="scss">
.floating-chat {
  position: fixed;
  z-index: 1000;
}

// 聊天窗口动画
.chat-slide-enter-active,
.chat-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.chat-slide-enter-from,
.chat-slide-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

// 聊天窗口
.chat-window {
  position: fixed;
  right: 24px;
  bottom: 88px;
  width: 380px;
  height: 600px;
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// 头部
.chat-header {
  padding: 16px;
  background: var(--ant-color-primary);
  color: var(--ant-color-text-inverse);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-text {
  .header-title {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }

  .header-status {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    opacity: 0.9;
    margin-top: 2px;

    .status-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      display: inline-block;

      &.online {
        background: #52c41a;
        animation: pulse-dot 2s infinite;
      }

      &.offline {
        background: #d9d9d9;
      }
    }
  }
}

@keyframes pulse-dot {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 4px;

  :deep(.ant-btn) {
    color: var(--ant-color-text-inverse);
    opacity: 0.85;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: var(--ant-color-text-inverse);
      opacity: 1;
      background: var(--ant-color-primary-hover);
    }

    .anticon {
      font-size: 16px;
    }
  }
}

// 消息区域
.chat-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: var(--ant-color-bg-layout);
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;

    &:hover {
      background: rgba(0, 0, 0, 0.2);
    }
  }
}

.connection-recovery {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 20px;
  background: var(--ant-color-fill-quaternary);
  color: var(--ant-color-text-secondary);

  span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
}

.empty-chat {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;

  .connect-guide {
    margin-top: 16px;
    text-align: center;
    p {
      margin-bottom: 8px;
      color: var(--ant-color-text-secondary);
    }
  }
}

.chat-messages {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message-timestamp {
  text-align: center;
  margin: 16px 0;
  color: var(--ant-color-text-secondary);
  font-size: 12px;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 60px;
    height: 1px;
    background: var(--ant-color-border);
  }

  &::before {
    right: calc(50% + 70px);
  }

  &::after {
    left: calc(50% + 70px);
  }
}

.message-wrapper {
  display: flex;
  gap: 8px;
  align-items: flex-end;

  &.user-message {
    flex-direction: row-reverse;
  }
}

.message-avatar {
  flex-shrink: 0;
}

.message-content {
  max-width: 70%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.message-bubble {
  padding: 10px 14px;
  border-radius: 16px;
  word-break: break-word;
  line-height: 1.5;
  font-size: 14px;
  transition: all 0.2s;
}

.user-message .message-bubble {
  background: linear-gradient(135deg, var(--ant-color-primary) 0%, var(--ant-color-primary-hover) 100%);
  color: var(--ant-color-text-inverse);
  border-bottom-right-radius: 4px;
}

.ai-message .message-bubble {
  background: var(--ant-color-bg-container);
  color: var(--ant-color-text);
  border-bottom-left-radius: 4px;
  border: 1px solid var(--ant-color-border);
}

.message-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.loading-indicator {
  align-self: flex-start;
}

.user-message .loading-indicator {
  align-self: flex-end;
}

// 输入区域
.chat-input {
  padding: 16px;
  background: var(--card-bg);
  border-top: 1px solid var(--border-color);
  flex-shrink: 0;
}

.input-wrapper {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: var(--bg-secondary);
  border-radius: 10px;
  border: 1px solid var(--border-color);
  transition: all 0.3s;

  &:focus-within {
    border-color: var(--ant-color-primary);
    box-shadow: 0 0 0 2px var(--ant-color-primary-bg);
  }
}

.mode-toggle {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ant-color-text-secondary);

  &.active {
    color: var(--ant-color-primary);
    background: var(--ant-color-primary-bg);
  }

  &:hover {
    background: var(--ant-color-fill-quaternary);
  }
}

:deep(.ant-input) {
  flex: 1;
  border: none;
  background: transparent;
  padding: 6px 8px;
  font-size: 14px;
  resize: none;

  &:focus {
    box-shadow: none;
  }

  &::placeholder {
    color: var(--ant-color-text-placeholder);
  }
}

.send-button {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  transform: scale(0.8);
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    background: var(--bg-disabled);
    border-color: var(--border-color);
  }
}

.record-button {
  flex: 1;
  height: 40px;
  border-radius: 20px;
  font-weight: 500;

  &.recording {
    background: #ff4d4f;
    border-color: #ff4d4f;
    animation: recording-pulse 1.5s infinite;
  }
}

@keyframes recording-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(255, 77, 79, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 77, 79, 0);
  }
}

// 响应式
@media (max-width: 768px) {
  .chat-window {
    right: 16px;
    bottom: 80px;
    width: calc(100vw - 32px);
    max-width: 380px;
    height: 500px;
  }
}

@media (max-width: 480px) {
  .chat-window {
    right: 8px;
    bottom: 72px;
    width: calc(100vw - 16px);
    height: calc(100vh - 100px);
  }
}

// 设置抽屉样式
.form-tip {
  color: var(--ant-color-text-secondary);
  font-size: 12px;
  line-height: 1.5;
}

.drawer-footer {
  display: flex;
  justify-content: flex-end;
  padding: 10px 16px;
  border-top: 1px solid var(--ant-color-border);
}

:deep(.ant-drawer) {
  .ant-drawer-header {
    border-bottom: 1px solid var(--ant-color-border);
  }

  .ant-drawer-body {
    padding: 24px;
  }

  .ant-drawer-footer {
    padding: 0;
    border-top: none;
  }
}

// 深色模式适配
html.dark,
html[data-theme='dark'] {
  .chat-content {
    &::-webkit-scrollbar-thumb {
      background: var(--ant-color-fill-quaternary);

      &:hover {
        background: var(--ant-color-fill-tertiary);
      }
    }
  }
}
</style>
